import type { FunctionComponent } from 'preact';
import { t } from '../../../../../domain/localisation';

interface MediapipeErrorViewProps {
  themeColor: string;
  hideAttribution: boolean;
  /** Disables Retry once the user has used up their attempts. */
  retriesExhausted: boolean;
  onRetry: () => void;
}

/**
 * Shown when the face-detection model can't be loaded and the flow has no
 * usable interval-capture path — strict mode, whose liveness mechanic *is* head-pose
 * detection, and smile mode when the partner has not opted into the interval-capture
 * capture via `allow_legacy_selfie_fallback`.
 * The network is usually fine here (the hold-up is
 * on-device setup), so only blame the connection when the browser actually
 * reports being offline.
 */
export const MediapipeErrorView: FunctionComponent<MediapipeErrorViewProps> = ({
  themeColor,
  hideAttribution,
  retriesExhausted,
  onRetry,
}) => {
  const isOffline =
    typeof navigator !== 'undefined' && navigator.onLine === false;
  const messageKey = isOffline
    ? 'selfie.capture.loading.offlineError'
    : 'selfie.capture.loading.setupError';

  return (
    <div className="ess-setup-error">
      <p className="setup-error-message" role="alert">
        {t(messageKey)}
      </p>
      <button
        type="button"
        className="btn-primary setup-error-retry"
        onClick={onRetry}
        disabled={retriesExhausted}
      >
        {t('selfie.capture.loading.retry')}
      </button>

      {!hideAttribution && (
        // @ts-expect-error preact-custom-element types
        <powered-by-smile-id />
      )}

      <style>{`
        :host { display: block; height: 100%; }
        .ess-setup-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          height: 100%;
          padding: 1.5rem 1rem;
          text-align: center;
          font-family: "DM Sans", system-ui, sans-serif;
          background: #F5F7FA;
          box-sizing: border-box;
        }
        .setup-error-message {
          margin: 0;
          font-size: 1rem;
          color: #151F72;
          max-width: 24rem;
        }
        .setup-error-retry {
          background-color: ${themeColor};
          border-radius: 2.5rem;
          color: white;
          border: none;
          height: 3.125rem;
          padding: 0.75rem 1.5rem;
          font-size: 1.125rem;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
        }
        .setup-error-retry:disabled {
          background-color: #666;
          cursor: not-allowed;
          opacity: 0.6;
        }
        .ess-setup-error > powered-by-smile-id {
          margin-top: auto;
          width: 100%;
        }
      `}</style>
    </div>
  );
};
